interface Block {
    top: number;
    bottom: number;
}

/**
 * #### Intersection option 1: (`itemTopInBetween`)
 *
 * | window.top | item.top | window.bottom | item.bottom |
 *
 * #### Intersection option 2: (`itemBottomInBetween`)
 *
 * | item.top | window.top | item.bottom | window.bottom |
 *
 * #### Window wraps the item: (both `itemTopInBetween` and `itemBottomInBetween` are true)
 *
 * | window.top | item.top | item.bottom | window.bottom |
 *
 * #### Item wraps the window:
 *
 * | item.top | window.top | window.bottom | item.bottom |
 *
 * @returns whether the bounds of the item intersects the bounds of the window
 */
function intersects(item: Block, window: Block): boolean {
    // If the window or the item has no height, they cannot intersect
    if (window.top === window.bottom || item.top === item.bottom) {
        return false;
    }

    const itemTopInBetween = item.top >= window.top && item.top < window.bottom;
    const itemBottomInBetween = item.bottom > window.top && item.bottom <= window.bottom;
    const itemWrapsWindow = item.top <= window.top && item.bottom >= window.bottom;

    return itemTopInBetween || itemBottomInBetween || itemWrapsWindow;
}

interface WithHeight {
    height: number;
}

interface WindowAroundOptions<T extends WithHeight> {
    items: T[];
    tolerance: number;
    windowHeight: number;
    startIndex: number;
}

interface VirtualScrollOptions<T extends WithHeight> {
    items: T[];
    tolerance: number;
    windowHeight: number;
    scrollPosition: number;
}

interface VirtualScrollResult<T> {
    placeholders: {
        top: number;
        bottom: number;
    };
    items: T[];
}

/**
 * Takes an array of items and returns a slice of original items and top and bottom placeholder heights to render to the screen,
 * depending on the scroll position and other parameters
 *
 * Example:
 *
 * items: [i1, i2, i3, i4, i5, i6, i7, i8, i9, 10]. Each has 10 px height
 *
 * Tolerance: 10, Window Height: 20, Scroll Position: 0
 *
 * Returns: items as [i1, i2, i3], placeholders as 0 and 70. i1 and i2 will be rendered and visible but i3 is rendered in the tolerance area.
 *
 * Another Example:
 *
 * items: [i1, i2, i3, i4, i5, i6, i7, i8, i9, 10]. Each has 10px height
 *
 * Tolerance: 10, Window Height: 20, Scroll Position: 30
 *
 * Returns: items as [i3, i4, i5, i6], placeholders as 20 and 30. i4 and i5 will be rendered and visible but i3 and i6 is rendered in the tolerance area.
 *
 * @param options.items An array of items with fixed heights
 * @param options.tolerance Height of the tolerance
 * @param options.windowHeight Height of the actual window area that is visible to the user
 * @param options.scrollPosition How much user scrolled the content
 * @returns object.items Slice of the options.items
 * @returns object.placeholders.top Height of the top placeholder area
 * @returns object.placeholders.bottom Height of the bottom placeholder area
 */
export function virtualScroll<T extends WithHeight>(
    options: VirtualScrollOptions<T>
): VirtualScrollResult<T> {
    const {
        items,
        tolerance,
        windowHeight,
        scrollPosition
    } = options;

    const windowTop = Math.max(scrollPosition - tolerance, 0);
    const windowBottom = scrollPosition + windowHeight + tolerance;
    const windowBlock: Block = {
        top: windowTop,
        bottom: windowBottom
    };

    let intersected = false;
    let currentYPosition = 0;
    const renderedItems = [];
    let topPlaceholderHeight = 0;
    let bottomPlaceholderHeight = 0;

    for (const item of items) {
        const itemBlock: Block = {
            top: currentYPosition,
            bottom: currentYPosition + item.height
        };

        if (intersects(itemBlock, windowBlock)) {
            intersected = true;
            renderedItems.push(item);
        } else if (!intersected) {
            topPlaceholderHeight += item.height;
        } else {
            bottomPlaceholderHeight += item.height;
        }

        currentYPosition += item.height;
    }

    return {
        placeholders: {
            top: topPlaceholderHeight,
            bottom: bottomPlaceholderHeight
        },
        items: renderedItems
    };
}

/**
 * Calculates the scroll position for given start index and executes the virtualScroll
 */
export function virtualScrollAroundIndex<T extends WithHeight>(
    options: WindowAroundOptions<T>
): VirtualScrollResult<T> {
    const {
        items,
        tolerance,
        windowHeight,
        startIndex
    } = options;

    const totalHeight = items.reduce((acc, item) => acc + item.height, 0);
    const sumOfItemHeightsAbove = items.slice(0, startIndex).reduce((acc, item) => acc + item.height, 0);
    const maxPossibleScrollPosition = totalHeight - windowHeight;
    const scrollPosition = Math.min(sumOfItemHeightsAbove, maxPossibleScrollPosition);

    return virtualScroll({
        items,
        tolerance,
        windowHeight,
        scrollPosition
    });
}
